import { useCallback, useEffect, useState, useContext } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import useUser, { getAuthorized, isUserClient, isUserDoctor } from "../../../hooks/User";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import dayjs from "dayjs";
import "./patientattemptdetail.css";

// Icons
const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m15 18-6-6 6-6"/>
    </svg>
);

const FileTextIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
        <polyline points="14 2 14 8 20 8"/>
    </svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
);

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
    </svg>
);

const HelpCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const MessageIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
);

const ImageIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
    </svg>
);

const MicIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        <line x1="12" y1="19" x2="12" y2="23"/>
        <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
);

// Status configurations
const StatusConfig = {
    completed: { 
        label: "Завершён", 
        className: "status-completed",
        icon: <CheckCircleIcon />
    },
    in_progress: { 
        label: "В процессе", 
        className: "status-in-progress",
        icon: <ClockIcon />
    },
    draft: { 
        label: "Черновик", 
        className: "status-draft",
        icon: <FileTextIcon />
    },
    overdue: { 
        label: "Просрочен", 
        className: "status-overdue",
        icon: <ClockIcon />
    }
};

// Answer Card Component
function AnswerCard({ answer, index }) {
    // API возвращает answer_text, а не text
    const hasText = answer.answer_text && answer.answer_text.trim();
    const hasImage = answer.picture_filename;
    const hasVoice = answer.voice_filename;

    return (
        <div className="answer-card">
            <div className="answer-header">
                <span className="answer-number">Вопрос {index + 1}</span>
                <span className="answer-order">#{answer.order_index + 1}</span>
            </div>
            
            <div className="answer-question">
                <HelpCircleIcon />
                <p>{answer.question_text}</p>
            </div>

            <div className="answer-content">
                {hasText ? (
                    <div className="answer-text">
                        <MessageIcon />
                        <p>{answer.answer_text}</p>
                    </div>
                ) : (
                    <div className="answer-empty">
                        <span>Нет текстового ответа</span>
                    </div>
                )}

                {hasImage && (
                    <div className="answer-media">
                        <ImageIcon />
                        <span>Изображение: {answer.picture_filename}</span>
                    </div>
                )}

                {hasVoice && (
                    <div className="answer-media">
                        <MicIcon />
                        <span>Голосовое сообщение: {answer.voice_filename}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

// Main Component
function PatientAttemptDetail() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setErrorList } = useContext(ErrorHandlerContext);
    
    const [attempt, setAttempt] = useState(null);
    const [loading, setLoading] = useState(true);

    const attemptId = location.state?.attemptId;

    const loadAttemptDetails = useCallback(async () => {
        if (!attemptId) {
            navigate(ROUTES.SURVEY_HISTORY, { viewTransition: true });
            return;
        }

        setLoading(true);
        try {
            // Загружаем базовую информацию о попытке
            const attemptsResponse = await getAuthorized("/client/my_attempts", {
                params: { limit: 100 }
            });
            
            const attempts = attemptsResponse.data?.attempts || [];
            const foundAttempt = attempts.find(a => a.attempt_id === attemptId);
            
            if (!foundAttempt) {
                setErrorList(prev => [...prev, "Попытка не найдена"]);
                setLoading(false);
                return;
            }
            
            // Загружаем детальную информацию о попытке
            const detailsResponse = await getAuthorized("/client/my_attempt", {
                params: { attempt_id: attemptId }
            });
            
            setAttempt({
                ...foundAttempt,
                details: detailsResponse.data
            });
        } catch (error) {
            console.error("Failed to load attempt details:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при загрузке деталей"]);
        } finally {
            setLoading(false);
        }
    }, [attemptId, navigate, setErrorList]);

    useEffect(() => {
        loadAttemptDetails();
    }, [loadAttemptDetails]);

    const handleBack = () => {
        navigate(ROUTES.SURVEY_HISTORY, { viewTransition: true });
    };

    if (loading) {
        return (
            <div className="attempt-detail-loading">
                <div className="spinner"></div>
                <p>Загрузка деталей...</p>
            </div>
        );
    }

    if (!attempt) {
        return (
            <div className="attempt-detail-error">
                <h3>Попытка не найдена</h3>
                <button className="btn-back" onClick={handleBack}>
                    <ArrowLeftIcon />
                    Вернуться к истории
                </button>
            </div>
        );
    }

    const status = StatusConfig[attempt.status] || StatusConfig.draft;
    const progress = attempt.total_questions_count > 0 
        ? Math.round((attempt.answered_questions_count / attempt.total_questions_count) * 100)
        : 0;

    const answeredQuestions = attempt.details?.answered_questions || [];
    const unansweredQuestions = attempt.details?.unanswered_questions || [];

    return (
        <div className="attempt-detail-container">
            <button className="btn-back-link" onClick={handleBack}>
                <ArrowLeftIcon />
                Назад к истории
            </button>

            <div className="attempt-detail-header">
                <div className="attempt-detail-title">
                    <FileTextIcon />
                    <div>
                        <h1>{attempt.survey_title}</h1>
                        {attempt.survey_description && (
                            <p className="survey-description">{attempt.survey_description}</p>
                        )}
                    </div>
                </div>

                <div className={`attempt-detail-status ${status.className}`}>
                    {status.icon}
                    <span>{status.label}</span>
                </div>
            </div>

            <div className="attempt-detail-stats">
                <div className="stat-card">
                    <span className="stat-label">Дата создания</span>
                    <span className="stat-value">
                        {dayjs(attempt.creation_dt).format("DD.MM.YYYY HH:mm")}
                    </span>
                </div>
                
                <div className="stat-card">
                    <span className="stat-label">Прогресс</span>
                    <div className="stat-progress">
                        <div className="progress-bar-large">
                            <div 
                                className="progress-fill-large" 
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <span className="progress-value">{progress}%</span>
                    </div>
                </div>

                <div className="stat-card">
                    <span className="stat-label">Отвечено вопросов</span>
                    <span className="stat-value">
                        {attempt.answered_questions_count} / {attempt.total_questions_count}
                    </span>
                </div>
            </div>

            <div className="attempt-answers-section">
                <h2 className="section-title">
                    <CheckCircleIcon />
                    Отвеченные вопросы ({answeredQuestions.length})
                </h2>
                
                {answeredQuestions.length > 0 ? (
                    <div className="answers-list">
                        {answeredQuestions.map((answer, index) => (
                            <AnswerCard 
                                key={answer.question_in_survey_id}
                                answer={answer}
                                index={index}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="empty-answers">
                        <p>Нет отвеченных вопросов</p>
                    </div>
                )}
            </div>

            {unansweredQuestions.length > 0 && (
                <div className="attempt-unanswered-section">
                    <h2 className="section-title unanswered">
                        <HelpCircleIcon />
                        Неотвеченные вопросы ({unansweredQuestions.length})
                    </h2>
                    
                    <div className="unanswered-list">
                        {unansweredQuestions.map((question, index) => (
                            <div key={question.question_in_survey_id} className="unanswered-item">
                                <span className="unanswered-number">{index + 1}</span>
                                <p>{question.question_text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Page Component
function PatientAttemptDetailPage() {
    const { data, loading, error } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        // Разрешаем доступ всем пользователям с ролью client (включая врачей)
        if (data && !isUserClient(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [data, error, loading, navigate]);

    if (loading) {
        return (
            <>
                <ProfileNavigation isDoctor={false} />
                <div className="attempt-detail-page">
                    <div className="attempt-detail-loading">
                        <div className="spinner"></div>
                        <p>Загрузка...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO title="Детали прохождения" description="Подробная информация о пройденном медицинском опросе" noindex={true} />
            <ErrorHandlerContextProvider>
                <ProfileNavigation isDoctor={isUserDoctor(data)} />
                <div className="attempt-detail-page">
                    <PatientAttemptDetail />
                </div>
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}

export default PatientAttemptDetailPage;
